import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  Box,
  styled,
  Container,
  ThemeProvider,
  Link,
} from '@material-ui/core';

import { useLazyQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { ORGANIZATIONS_BY_PREFIX_QUERY } from '@/apollo/queries';
import { OverrideToggle } from '@/pages/Onboarding/components/OverrideToggle/OverrideToggle';
import { SleepioHeader } from '@/pages/Onboarding/components/Header/SleepioHeader';
import {
  getProductName,
  getTheme,
  isSpark as isSparkHelper,
  Product,
} from '@/utils/productHelpers';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';

interface Organization {
  name?: string | null;
  slug: string;
}

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: '100vh',
});

const SuggestionsContainer = styled(Paper)({
  position: 'absolute',
  zIndex: 1000,
  width: '100%',
  maxHeight: 200,
  overflow: 'auto',
  marginTop: 4,
});

const InputContainer = styled(Box)({
  position: 'relative',
  marginBottom: 32,
});

const SupportText = styled(Typography)({
  fontSize: 16,
  fontWeight: 400,
  lineHeight: '135%',
  textAlign: 'center',
  marginTop: 16,
});

const DROPDOWN_RESULTS_LIMIT = 3;

const CheckYourOrganization = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { product } = useParams<{ product: Product }>();
  const isEmployersRoute = window.location.pathname.includes('/employers');
  const lastRequestId = useRef(0);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [returnError, setReturnError] = useState(false);

  const [searchOrganizations, { loading: searchLoading }] = useLazyQuery(ORGANIZATIONS_BY_PREFIX_QUERY);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedOrganization || searchQuery.length < 2) {
        setOrganizations([]);
        setShowSuggestions(false);
        return;
      }

      lastRequestId.current += 1;
      const requestId = lastRequestId.current;

      searchOrganizations({
        variables: {
          prefix: searchQuery,
          limit: DROPDOWN_RESULTS_LIMIT,
          searchFilters: JSON.stringify({ organization_type: isEmployersRoute ? 'emp' : 'prov' }),
        },
        onCompleted: (data) => {
          if (requestId !== lastRequestId.current) { return; }
          setOrganizations(data?.organizationsByPrefix || []);
          setShowSuggestions(true);
        },
        onError: () => {
          setReturnError(true);
          setOrganizations([]);
        },
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, selectedOrganization, searchOrganizations, isEmployersRoute]);

  const handleInputChange = (value: string) => {
    setSearchQuery(value);
    setReturnError(false);

    // If user clears the input or changes it, clear selection
    if (selectedOrganization && value !== (selectedOrganization.name || '')) {
      setSelectedOrganization(null);
    }
  };

  const handleOrganizationSelect = (organization: Organization) => {
    setSelectedOrganization(organization);
    const orgName = organization.name || '';
    setSearchQuery(orgName);
    setShowSuggestions(false);
  };

  const renderSuggestionContent = () => {
    if (searchLoading) {
      return (
        <ListItem>
          <ListItemText primary={t('coverage.pre_onboarding.organization.search')} />
        </ListItem>
      );
    }
    if (organizations.length > 0) {
      return (
        <List>
          {organizations.map((organization) => (
            <ListItem
              key={organization.slug}
              button
              onMouseDown={(e) => {
                e.preventDefault();
                handleOrganizationSelect(organization);
              }}
            >
              <ListItemText primary={organization.name || 'Unnamed Organization'} />
            </ListItem>
          ))}
        </List>
      );
    }
    return (
      <ListItem>
        <ListItemText primary={t('coverage.pre_onboarding.organization.error')} />
      </ListItem>
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (returnError) {
      console.error('Intentional error produced by the override toggle');
      return;
    }

    if (selectedOrganization) {
      history.push(`/activate/${product}/${selectedOrganization.slug}`);
    }
  };

  const flowType = isEmployersRoute ? 'specific' : 'generic';
  const isSpark = isSparkHelper(product);
  const productType = isSpark ? 'spark' : 'sleepio';
  const title = `coverage.pre_onboarding.${flowType}.associated.${productType}`;
  const subtitle = `coverage.pre_onboarding.${flowType}.organization_subtitle.${productType}`;
  const organizationLabel = `coverage.pre_onboarding.${flowType}.organization_label`;
  const organizationPlaceholder = `coverage.pre_onboarding.${flowType}.organization_placeholder`;
  const supportEmail = isSpark
    ? t('coverage.spark.patient.info_popup.email_spark')
    : t('coverage.sleepio.support.email');

  const theme = getTheme(product);

  return (
    <ThemeProvider theme={theme}>
      <ContentContainer>
        <SleepioHeader />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <>
            <Typography variant="h2" style={{ marginTop: 32 }} color="textPrimary">
              {t(title, { product: getProductName(product) })}
            </Typography>
            <Typography style={{ marginTop: 8, marginBottom: 32 }} color="textPrimary">
              {t(subtitle, { productName: getProductName(product) })}
            </Typography>
            <form onSubmit={handleSubmit}>
              <InputContainer>
                <TextField
                  data-testid="organization-input"
                  autoFocus
                  variant="outlined"
                  fullWidth
                  required
                  color="secondary"
                  label={t(organizationLabel)}
                  placeholder={t(organizationPlaceholder)}
                  value={searchQuery}
                  onChange={(e) => handleInputChange(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
                {(showSuggestions || returnError) && (
                  <SuggestionsContainer elevation={3}>
                    {returnError ? (
                      <ListItem>
                        <ListItemText primary={t('errors.generic_support_error')} />
                      </ListItem>
                    ) : (
                      renderSuggestionContent()
                    )}
                  </SuggestionsContainer>
                )}
              </InputContainer>

              <Button
                variant="contained"
                role="button"
                data-testid="submit-button"
                type="submit"
                fullWidth
                color="primary"
                disabled={!selectedOrganization}
                rounded
              >
                {t('coverage.pre_onboarding.organization.check_submit')}
              </Button>
            </form>
            <SupportText color="textPrimary">
              {t('coverage.pre_onboarding.organization.support_text')}
              {' '}
              <Link
                href={`mailto:${supportEmail}`}
                underline="always"
                color="inherit"
              >
                {supportEmail}
              </Link>
            </SupportText>
            {process.env.NODE_ENV !== 'production' && (
              <OverrideToggle
                userMessage="Simulate server error"
                override={setReturnError}
              />
            )}
          </>
          <ConsentWidget />
        </Container>
        <Footer />
      </ContentContainer>
    </ThemeProvider>
  );
};

export default CheckYourOrganization;
